import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import getFullNameWithTitle from '@medflex/utils/getFullNameWithTitle';
import useClassNames from '@medflex/hooks/useClassNames';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import SessionsDetailModalSectionTitle from './SessionsDetailModalSectionTitle';
import SessionsParticipantList from '../SessionsParticipantList';
import SessionsParticipantListItem from '../SessionsParticipantListItem';
import SessionsAddParticipantForm from '../SessionsAddParticipantForm';
import Button from '../../Button';

const SessionsDetailModalInvitees = ({
  onSubmit,
  isEditable,
  invitees,
  onDelete,
  contacts,
  maxParticipants,
  minParticipants,
  disabled,
  className,
  title,
  contactsOnly,
  canSendSms,
  smsUpgradeUrl,
}) => {
  const [composing, setComposing] = useState(false);
  const { t } = useTranslation('components');
  const [participantToAdd, setParticipantToAdd] = useState(null);

  const cn = useClassNames(() => ['mb-6', className], [className]);

  const handleToggle = () => setComposing(!composing);

  const handleParticipantAdded = data => {
    if (
      !participantToAdd ||
      Object.keys(participantToAdd).some(key => participantToAdd[key] !== data[key])
    ) {
      setParticipantToAdd(data);
    }
  };

  const handleParticipantRemoved = () => {
    setParticipantToAdd(null);
  };

  const handleSubmit = event => {
    onSubmit(participantToAdd, event);
    setParticipantToAdd(null);
  };

  return (
    <section data-component="SessionsDetailModalInvitees" className={cn}>
      {title && <SessionsDetailModalSectionTitle>{title}</SessionsDetailModalSectionTitle>}
      {isEditable && !composing && invitees.length < maxParticipants && (
        <button
          type="button"
          onClick={handleToggle}
          className="focus:outline-none flex items-center mt-2 space-x-2 text-blue-500 hover:text-blue-700"
        >
          <PlusCircleIcon className="h-6 w-6" />
          <div>{t('Sessions.SessionsDetailModal.SessionsDetailModalInvitees.add')}</div>
        </button>
      )}
      {composing && (
        <div className="p-1 mt-4">
          {participantToAdd && participantToAdd.userId && (
            <SessionsParticipantList className="mb-2">
              <SessionsParticipantListItem
                key={participantToAdd.userId || participantToAdd.reference}
                reference={participantToAdd.reference}
                onDelete={handleParticipantRemoved}
              >
                {participantToAdd.fullNameWithTitle ||
                  getFullNameWithTitle({ id: participantToAdd.userId, ...participantToAdd })}
              </SessionsParticipantListItem>
            </SessionsParticipantList>
          )}
          {!(participantToAdd && participantToAdd.userId) && (
            <SessionsAddParticipantForm
              onSubmit={handleParticipantAdded}
              onDeleteParticipant={handleParticipantRemoved}
              contacts={contacts}
              maxParticipants={2}
              invitees={invitees}
              contactsOnly={contactsOnly}
              canSendSms={canSendSms}
              smsUpgradeUrl={smsUpgradeUrl}
            />
          )}
          <div className="flex justify-end mt-4 text-sm space-x-4">
            <Button onClick={handleToggle} kind="secondary:ghost">
              {t('Sessions.SessionsDetailModal.SessionsDetailModalInvitees.close')}
            </Button>
            <Button isDisabled={disabled} onClick={handleSubmit}>
              {t('Sessions.SessionsDetailModal.SessionsDetailModalInvitees.save')}
            </Button>
          </div>
        </div>
      )}
      <SessionsParticipantList className="mt-4">
        {invitees &&
          invitees.map(participant => (
            <SessionsParticipantListItem
              key={participant.participantId || participant.reference}
              reference={participant.reference}
              onDelete={() => onDelete(participant)}
              readOnly={
                !isEditable ||
                invitees.length <= minParticipants ||
                (participant.roles && participant.roles.includes('moderator'))
              }
            >
              {participant.fullNameWithTitle ||
                getFullNameWithTitle({ id: participant.userId, ...participant })}
            </SessionsParticipantListItem>
          ))}
      </SessionsParticipantList>
    </section>
  );
};

SessionsDetailModalInvitees.propTypes = {
  title: PropTypes.string,
  onSubmit: PropTypes.func,
  onDelete: PropTypes.func,
  isEditable: PropTypes.bool,
  disabled: PropTypes.bool,
  maxParticipants: PropTypes.number,
  minParticipants: PropTypes.number,
  contactsOnly: PropTypes.bool,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.array,
    PropTypes.object,
  ]),
  invitees: PropTypes.arrayOf(
    PropTypes.shape({
      reference: PropTypes.string,
      fullNameWithTitle: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      avatar: PropTypes.string,
      initials: PropTypes.string,
      participantId: PropTypes.string,
      roles: PropTypes.arrayOf(PropTypes.oneOf(['moderator'])),
    }),
  ),
  contacts: PropTypes.arrayOf(
    PropTypes.shape({
      userId: PropTypes.string,
      fullNameWithTitle: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      avatar: PropTypes.string,
      initials: PropTypes.string,
    }),
  ),
  canSendSms: PropTypes.bool.isRequired,
  smsUpgradeUrl: PropTypes.string,
};

SessionsDetailModalInvitees.defaultProps = {
  title: null,
  onSubmit: () => {},
  onDelete: () => {},
  maxParticipants: 9,
  minParticipants: 1,
  contactsOnly: false,
  smsUpgradeUrl: '',
};

export default SessionsDetailModalInvitees;
